import { useState } from 'react';
import { WorkbookPage, SectionTitle, Callout } from '@/components/WorkbookPage';
import { TrendingDown, Plus } from 'lucide-react';

export const DebtTrackerPage = () => {
  const [debts, setDebts] = useState([
    { name: '', balance: '', minPayment: '', apr: '', dueDate: '' },
    { name: '', balance: '', minPayment: '', apr: '', dueDate: '' },
    { name: '', balance: '', minPayment: '', apr: '', dueDate: '' },
    { name: '', balance: '', minPayment: '', apr: '', dueDate: '' },
    { name: '', balance: '', minPayment: '', apr: '', dueDate: '' },
    { name: '', balance: '', minPayment: '', apr: '', dueDate: '' },
  ]);
  
  const updateDebt = (index, field, value) => {
    const newDebts = [...debts];
    newDebts[index] = { ...newDebts[index], [field]: value };
    setDebts(newDebts);
  };
  
  const addDebt = () => {
    setDebts([...debts, { name: '', balance: '', minPayment: '', apr: '', dueDate: '' }]);
  };
  
  return (
    <WorkbookPage pageId="debt-tracker" prevPage="check-in-sheets" nextPage="credit-checklist">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <TrendingDown className="h-6 w-6 text-primary" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Debt Tracker
          </h1>
        </div>
        <p className="text-muted-foreground">
          List all debts from smallest to largest for snowball method
        </p>
      </header>
      
      <Callout type="tip">
        <strong>Snowball Order:</strong> List debts from smallest balance to largest. 
        Attack the smallest first while paying minimums on the rest.
      </Callout>
      
      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-border">
              <th className="text-left py-3 pr-2">Creditor/Debt</th>
              <th className="text-right py-3 px-2">Balance</th>
              <th className="text-right py-3 px-2">Min Payment</th>
              <th className="text-right py-3 px-2">APR %</th>
              <th className="text-center py-3 pl-2">Due Date</th>
            </tr>
          </thead>
          <tbody>
            {debts.map((debt, index) => (
              <tr key={index} className="border-b border-border/50">
                <td className="py-3 pr-2">
                  <input
                    type="text"
                    className="w-full bg-muted/30 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Credit card, loan, etc."
                    value={debt.name}
                    onChange={(e) => updateDebt(index, 'name', e.target.value)}
                  />
                </td>
                <td className="py-3 px-2">
                  <input
                    type="text"
                    className="w-full text-right bg-muted/30 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="$___"
                    value={debt.balance}
                    onChange={(e) => updateDebt(index, 'balance', e.target.value)}
                  />
                </td>
                <td className="py-3 px-2">
                  <input
                    type="text"
                    className="w-full text-right bg-muted/30 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="$___"
                    value={debt.minPayment}
                    onChange={(e) => updateDebt(index, 'minPayment', e.target.value)}
                  />
                </td>
                <td className="py-3 px-2">
                  <input
                    type="text"
                    className="w-full text-right bg-muted/30 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="___%"
                    value={debt.apr}
                    onChange={(e) => updateDebt(index, 'apr', e.target.value)}
                  />
                </td>
                <td className="py-3 pl-2">
                  <input
                    type="text"
                    className="w-full text-center bg-muted/30 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="15th"
                    value={debt.dueDate}
                    onChange={(e) => updateDebt(index, 'dueDate', e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <button
        onClick={addDebt}
        className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add another debt
      </button>
      
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-lg p-4">
          <label className="block text-sm font-medium mb-2">Total Debt Balance:</label>
          <input
            type="text"
            className="w-full bg-card rounded-lg px-4 py-2 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="$____"
          />
        </div>
        <div className="bg-muted/30 rounded-lg p-4">
          <label className="block text-sm font-medium mb-2">Total Minimum Payments:</label>
          <input
            type="text"
            className="w-full bg-card rounded-lg px-4 py-2 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="$____/mo"
          />
        </div>
      </div>
      
      <div className="mt-6 bg-success/10 rounded-lg p-4 border border-success/20">
        <label className="block text-sm font-medium mb-2">My Debt Payoff Target:</label>
        <input
          type="text"
          className="w-full bg-card rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="I want to be debt-free by [date] or pay off [specific debt] by [date]"
        />
      </div>
    </WorkbookPage>
  );
};
